import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { identifyPostHogUser } from "@/lib/posthog-user";

interface UserState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  user: null,
  isLoading: false,
  error: null,
};
if (typeof window !== "undefined" && localStorage.getItem("user")) {
  initialState.user = JSON.parse(localStorage.getItem("user")!);
}

export const fetchUser = createAsyncThunk(
  "user/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const { fetchData } = await import("@/utils/api-handler");
      const response = await fetchData("/api/auth/me");
      localStorage.setItem("user", JSON.stringify(response));
      const { syncDefaultSubscriptionPlanCookieFromUser } = await import(
        "@/services/auth-service"
      );
      syncDefaultSubscriptionPlanCookieFromUser(response);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch user"
      );
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      if (action.payload) {
        localStorage.setItem("user", JSON.stringify(action.payload));
      } else {
        localStorage.removeItem("user");
      }
    },
    setUserLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
        identifyPostHogUser(action.payload);
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, setUserLoading } = userSlice.actions;
export default userSlice.reducer;
